// @ts-nocheck
/* eslint-disable no-console */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { Button, Div, Icon, Input, Tag, Text, Textarea } from '@pinpoint/design-kit';

import { RefreshesDropdown } from 'src/components';
import { DialogActionProp } from 'src/store/typings';
import useRefreshes from 'src/hooks/swr/useRefreshes';
import { fetchFrameworks } from 'src/store/api';
import CONSTANTS from 'src/store/constants';
import BaseDialog from './base';

const defaultValues = {
  id: '',
  features: '',
  notes: '',
  refreshes: [],
  regulationTags: [],
};

function FrameworkDialog({ isOpen, actions, title, framework }) {
  const [featuresExpanded, setFeaturesExpanded] = useState(false);

  const refreshesQuery = useMemo(() => {
    const query = {
      filters: [{ name: 'platform', op: 'eq', val: CONSTANTS.PLATFORM.REFRESH_VERSIONS.UPGRADED }],
      order_by: [
        {
          field: 'created_at',
          direction: 'asc',
        },
      ],
      fields: 'items{id,name,platform},*',
    };
    return query;
  }, []);

  const { data: refreshes = [] } = useRefreshes({ query: refreshesQuery, config: { revalidateOnMount: true } });

  const [regulationTagsInputValue, setRegulationTagsInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  const { handleSubmit, errors, control, reset, setError, clearErrors } = useForm({ defaultValues });

  const onSubmit = useCallback(
    async (data) => {
      const newFramework = {
        ...data,
        refreshes: data.refreshes.map((refresh) => refresh.name),
      };
      try {
        setLoading(true);
        await actions.handlers[0].on(newFramework);
      } catch (error) {
        console.error(error);
      }
      actions.handlers[0].after();
      actions.close.on();
      setLoading(false);
    },
    [actions.close, actions.handlers]
  );
  const formSubmit = handleSubmit(onSubmit);

  const resetForm = useCallback(() => {
    reset(defaultValues);
    setRegulationTagsInputValue('');
  }, [reset]);

  const handleKeyDown = useCallback(
    (e, value, onChange) => {
      const isValidRegulationTag = (tag) => {
        const validLinesOfBusiness = CONSTANTS.REGULATORY.LINES_OF_BUSINESS.join('|');
        const validStateCodes = CONSTANTS.GENERAL.FORM.US_STATES.join('|');
        const tagRegex = new RegExp(`^(${validStateCodes})-(${validLinesOfBusiness})?$`, 'i');
        const match = tag.match(tagRegex);
        if (!match) return false;
        const [, state, lineOfBusiness] = match;
        return (
          CONSTANTS.GENERAL.FORM.US_STATES.includes(state) &&
          CONSTANTS.REGULATORY.LINES_OF_BUSINESS.includes(lineOfBusiness)
        );
      };

      const trimmed = regulationTagsInputValue.trim();

      if ((e.key === 'Enter' || e.key === ',' || e.key === 'Tab') && trimmed) {
        e.preventDefault();

        const regulationTagErrors = [];

        if (!isValidRegulationTag(trimmed)) {
          regulationTagErrors.push(
            `Invalid regulation tag format. Must be a valid state code with a line of business (e.g. CA-PAU, or CA-HO). Lines of business: ${CONSTANTS.REGULATORY.LINES_OF_BUSINESS.join(
              ', '
            )}`
          );
        }

        if (value.includes(trimmed)) {
          regulationTagErrors.push('Regulation tag already added');
        }

        if (regulationTagErrors.length) {
          setError('regulationTags', { type: 'manual', message: regulationTagErrors.join(' - ') });
          onChange([...value]);
          return;
        }

        clearErrors('regulationTags');
        onChange([...value, trimmed]);
        setRegulationTagsInputValue('');
      }

      if ((e.key === 'Enter' || e.key === ',' || e.key === 'Tab') && !trimmed && value.length) {
        e.preventDefault();
        clearErrors('regulationTags');
        onChange([...value]);
        setRegulationTagsInputValue('');
      }

      if (e.key === 'Backspace' && !regulationTagsInputValue && value.length) {
        e.preventDefault();
        onChange(value.slice(0, -1));
      }
    },
    [clearErrors, regulationTagsInputValue, setError]
  );

  const removeChip = useCallback(
    (index, value, onChange) => {
      const updated = value.filter((_, i) => i !== index);
      onChange(updated);
      clearErrors('regulationTags');
    },
    [clearErrors]
  );

  const buttonText = useMemo(() => {
    if (loading && framework)
      return <Icon color="shade66" m={{ x: '.4rem' }} name="Loading4" viewBox="0 0 18 1" size="1rem" />;
    if (framework) return 'SAVE';
    if (loading) return <Icon color="shade66" m={{ x: '.85rem' }} name="Loading4" viewBox="0 0 18 1" size="1rem" />;
    return 'CREATE';
  }, [framework, loading]);

  useEffect(() => {
    if (framework) {
      reset({
        id: framework.id,
        features: framework.features,
        notes: framework.notes,
        refreshes: framework.refreshes.map((refresh) => ({ name: refresh })),
        regulationTags: framework.regulationTags,
      });
    }
  }, [framework, reset]);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  return (
    <BaseDialog
      isOpen={isOpen}
      hideCloseIcon
      actions={{
        ...actions,
        cancel: {
          ...actions.cancel,
          before: framework ? null : resetForm,
        },
        close: {
          ...actions.close,
          before: framework ? null : resetForm,
        },
        handlers: [
          {
            on: formSubmit,
          },
        ],
      }}
      items={{
        buttons: [
          {
            text: buttonText,
            textSize: 'body',
            close: false,
          },
        ],
        content: [
          <Div key="form-1" d="flex" flexDir="column">
            {!framework && (
              <>
                <Text textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem' }}>
                  Enter id
                  <Icon
                    name="Info"
                    size="0.75rem"
                    color="brand500"
                    m={{ l: '.5em', b: '-0.125rem' }}
                    data-tip="The id must be a number and not already in use. It will have a prefix added to create the corresponding framework name (e.g. F1)."
                  />
                </Text>
                <Controller
                  name="id"
                  data-test="framework-id"
                  control={control}
                  as={Input}
                  borderColor="shade79"
                  m={{ y: '.375rem' }}
                  h="1.875rem"
                  rules={{
                    valueAsNumber: true,
                    required: 'Id is required',
                    min: { value: 1, message: 'Id must be greater than 0' },
                    pattern: {
                      value: /^[0-9]+$/,
                      message: 'Id must be a number',
                    },
                    validate: async (value) => {
                      if (framework && value === framework?.id) return true;
                      const query = {
                        filters: [{ name: 'id', op: 'eq', val: value }],
                        order_by: [
                          {
                            field: 'created_at',
                            direction: 'desc',
                          },
                        ],
                      };
                      const options = {
                        headers: {
                          'X-Fields': 'items{id},*',
                        },
                      };
                      const frameworks = await fetchFrameworks(query, options);
                      return frameworks.length ? 'Id is already taken' : true;
                    },
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name="id"
                  data-test="framework-id-error"
                  render={({ message }) => {
                    if (![undefined, null, ''].includes(message)) {
                      return (
                        <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                          {message}
                        </Text>
                      );
                    }
                    return null;
                  }}
                />
              </>
            )}
          </Div>,

          <Div key="form-2" d="flex" flexDir="column">
            {!framework && (
              <>
                <Text textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem' }}>
                  Enter feature set
                  <Icon
                    name="Info"
                    size="0.75rem"
                    color="brand500"
                    m={{ l: '.5em', b: '-0.125rem' }}
                    data-tip="List of features that will be applied to modeling. This must be a comma separated list of features. No trailing comma is allowed. Feature set may not exist in another framework."
                  />
                </Text>
                <Controller
                  name="features"
                  data-test="framework-features"
                  control={control}
                  as={Input}
                  borderColor="shade79"
                  m={{ y: '.375rem' }}
                  h="1.875rem"
                  rules={{
                    required: 'Feature set is required',
                    validate: {
                      commaSeparated: (value) =>
                        /^[^,\s][^,]*?(?:,\s?[^,\s][^,]*?)*$/.test(value) ||
                        `The features must be a comma separated list. No trailing comma is allowed.`,
                    },
                  }}
                />
                <ErrorMessage
                  errors={errors}
                  name="features"
                  data-test="framework-features-error"
                  render={({ message }) => {
                    if (![undefined, null, ''].includes(message)) {
                      return (
                        <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                          {message}
                        </Text>
                      );
                    }
                    return null;
                  }}
                />
              </>
            )}
          </Div>,

          <Div key="form-3" d="flex" flexDir="column">
            <Text textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem' }}>
              Enter notes
            </Text>
            <Controller
              name="notes"
              data-test="framework-notes"
              control={control}
              as={Textarea}
              type="textarea"
              border="1px solid"
              borderColor="shade79"
              m={{ t: '.375rem' }}
              h="10rem"
              textWeight="400"
              style={{
                resize: 'none',
                fontFamily: 'Roboto',
                background: 'transparent',
                opacity: '1',
              }}
            />
            <ErrorMessage
              errors={errors}
              name="notes"
              data-test="framework-notes-error"
              render={({ message }) => {
                if (![undefined, null, ''].includes(message)) {
                  return (
                    <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                      {message}
                    </Text>
                  );
                }
                return null;
              }}
            />
          </Div>,

          <Div key="form-4" d="flex" flexDir="column">
            <Text textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem' }}>
              Select refreshes
            </Text>
            <Controller
              name="refreshes"
              control={control}
              render={({ onChange, value }) => (
                <RefreshesDropdown
                  data-test="framework-refreshes-dropdown"
                  dropDownStyle={{
                    w: '100%',
                    h: '1.875rem',
                    m: { y: '.375rem' },
                    DropdownButtonOptions: {
                      h: '1.875rem',
                      w: '1.875rem',
                      rounded: { tr: 'md', br: 'md' },
                      border: { y: '1px solid', r: '1px solid', l: '0' },
                      borderColor: 'shade79',
                      align: 'center',
                      justify: 'center',
                      d: 'flex',
                      p: '0',
                    },
                    inputStyle: {
                      h: 'calc(1.875rem-1px)',
                      flexGrow: '1',
                      p: { l: '0' },
                      rounded: 'none',
                      'data-test': 'framework-refreshes-dropdown-input',
                    },
                    menuItemOptions: {
                      d: 'flex',
                      align: 'center',
                      p: { x: '1rem', y: '0.5rem' },
                      hoverBg: 'gray100',
                      bg: 'white',
                      'data-test': 'framework-refreshes-dropdown-menu-item',
                    },
                    DivOptions: {
                      rounded: { l: 'md', r: '0' },
                      border: '1px solid',
                      borderColor: 'shade79',
                      p: { l: '0.75rem' },
                    },
                    openSuffix: <Icon color="shade66" name="UpArrow" size=".625rem" />,
                    closeSuffix: <Icon color="shade66" name="DownArrow" size=".625rem" />,
                  }}
                  refreshes={refreshes}
                  refresh={value}
                  valueIndices={
                    framework
                      ? framework.refreshes.map((refresh) => refreshes.findIndex(({ name }) => name === refresh))
                      : []
                  }
                  defaultText="Please select refreshes"
                  onSelectRefresh={(selectedValues) => onChange(selectedValues)}
                  multiSelect
                />
              )}
              rules={{
                validate: {
                  refreshesRequired: (value) => (value && value.length > 0) || 'Refreshes are required',
                },
              }}
            />
            <ErrorMessage
              errors={errors}
              name="refreshes"
              data-test="framework-refreshes-error"
              render={({ message }) => {
                if (![undefined, null, ''].includes(message)) {
                  return (
                    <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                      {message}
                    </Text>
                  );
                }
                return null;
              }}
            />
          </Div>,

          <Div key="form-5" d="flex" flexDir="column">
            <Text textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem' }}>
              Enter State-LOB tags
              <Icon
                name="Info"
                size="0.75rem"
                color="brand500"
                m={{ l: '.5em', b: '-0.125rem' }}
                data-tip={`Tags are designated by state and lines of business. For each line of business in a state, add a tag with the format [State-LOB] (e.g. CA-PAU, CA-HO). Only ${CONSTANTS.REGULATORY.LINES_OF_BUSINESS.join(
                  ', '
                )} are currently allowed.`}
              />
            </Text>
            <Controller
              name="regulationTags"
              control={control}
              render={({ onChange, value }) => (
                <>
                  <Div
                    data-test="regulation-tags-container"
                    w="100%"
                    h="1.875rem"
                    d="flex"
                    border="1px solid"
                    borderColor="shade79"
                    rounded="md"
                    flexGrow="1"
                    flexWrap="wrap"
                    align="center"
                    p={{ x: '0.75rem' }}
                    m={{ y: '.375rem' }}
                  >
                    {value.map((tag, index) => (
                      <Tag
                        key={tag}
                        data-test={`regulation-tag-${tag}`}
                        m={{ x: '3.2px', y: '3.2px' }}
                        h="24px"
                        whiteSpace="nowrap"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        suffix={
                          <Button
                            data-tip="Remove"
                            data-test={`regulation-tag-${tag}-remove`}
                            bg="transparent"
                            border="none"
                            p="1.6px"
                            type="button"
                            onClick={(e) => {
                              removeChip(index, value, onChange);
                              e.stopPropagation();
                            }}
                          >
                            <Icon size="12px" name="Cross" />
                          </Button>
                        }
                      >
                        {tag}
                      </Tag>
                    ))}
                    <Input
                      data-test="regulation-tags-input"
                      d="flex"
                      flexGrow="1"
                      minW="100px"
                      border="none"
                      p={{ l: value.length > 0 ? '11px' : 0, r: '2px' }}
                      rounded="none"
                      h="1.875rem"
                      value={regulationTagsInputValue}
                      onChange={(e) => setRegulationTagsInputValue(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, value, onChange)}
                      placeholder="Type and press Enter"
                    />
                  </Div>
                  <ErrorMessage
                    errors={errors}
                    name="regulationTags"
                    data-test="framework-regulation-tags-error"
                    render={({ message }) => {
                      if (![undefined, null, ''].includes(message)) {
                        return (
                          <Text textColor="danger800" textSize="caption" m={{ t: '.125rem' }}>
                            {message}
                          </Text>
                        );
                      }
                      return null;
                    }}
                  />
                </>
              )}
              rules={{
                validate: {
                  regulationTagsRequired: (value) => (value && value.length > 0) || 'Regulation tags are required',
                },
              }}
            />
          </Div>,

          <Div key="form-6" d="flex" flexDir="column">
            {framework && (
              <>
                <Text textSize="input" textWeight="500" textColor="shade66" m={{ t: '1.25rem' }}>
                  Features
                </Text>
                <Div
                  data-test="framework-features"
                  w="100%"
                  h={featuresExpanded ? 'calc(50vh - 10rem)' : 'calc(50vh - 35vh)'}
                  maxH="100%"
                  overflow="auto"
                  m={{ y: '.5rem' }}
                  transition="all 0.2s ease-in-out"
                >
                  {framework.features.split(',').map((f) => (
                    <Text key={f} textSize="body">
                      {f}
                    </Text>
                  ))}
                </Div>
                <Text
                  data-test="framework-features-expand"
                  d="flex"
                  align="center"
                  textSize="body"
                  textWeight="500"
                  textColor="blue"
                  cursor="pointer"
                  onClick={() => setFeaturesExpanded(!featuresExpanded)}
                >
                  {featuresExpanded ? 'Collapse' : 'Expand'}
                  {featuresExpanded ? (
                    <Icon name="UpArrow" size="0.75rem" m={{ l: '.5em' }} color="blue" />
                  ) : (
                    <Icon name="DownArrow" size="0.75rem" m={{ l: '.5em' }} color="blue" />
                  )}
                </Text>
              </>
            )}
          </Div>,
        ],
      }}
      title={
        title ?? {
          text: 'Create a Framework',
          backgroundColor: 'orange',
          color: 'white',
          textSize: 'title',
          icon: (
            <Icon
              name="AddBoxed"
              viewBox="0 0 14 14"
              size="2.375rem"
              color="white"
              alignSelf="center"
              justifySelf="center"
            />
          ),
        }
      }
    />
  );
}

FrameworkDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  actions: DialogActionProp.isRequired,
  // eslint-disable-next-line react/require-default-props
  title: PropTypes.shape({
    text: PropTypes.string.isRequired,
    textSize: PropTypes.string,
    backgroundColor: PropTypes.string.isRequired,
    color: PropTypes.string,
    icon: PropTypes.element,
  }),
  // eslint-disable-next-line react/require-default-props
  framework: PropTypes.shape({
    id: PropTypes.number,
    features: PropTypes.string,
    notes: PropTypes.string,
    refreshes: PropTypes.arrayOf(PropTypes.string),
    regulationTags: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default FrameworkDialog;
